import { Button, ButtonColors, ButtonVariants } from "@inno-ui/button";
import { Icon, IconSizes, PlusIcon } from "@inno-ui/icons";
import { toast } from "@inno-ui/toast";
import { format } from "date-fns";
import { de, enUS, es, fr, it, pt, zhCN } from "date-fns/locale";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import bodyHoldingClipboard from "../../../../assets/boy-holding-clipboard.png";
import { useGetAvailableTimeSlots } from "../../../../hooks/api/use-get-available-time-slots";
import { useAppointmentStore } from "../../../../store/appointment/appointment-store";
import { useGeneralStore } from "../../../../store/general/general-store";
import { Language } from "../../../../utils/enums";
import styles from "./appointment-confirmation-mobile.module.scss";

export function AppointmentConfirmationMobile() {
  const { t, i18n } = useTranslation();
  const {
    userAppointmentData,
    appointmentData: { date, timeRange },
    resetOtherParticipants,
    resetTimeRange,
    setTimeIntervals,
    dateToDisplay,
  } = useAppointmentStore();
  const { currentTimezone, setIsConfirmationPage } = useGeneralStore();
  const { reset } = useGeneralStore();
  const { refetch, isLoading: isApiLoading } = useGetAvailableTimeSlots(dateToDisplay);
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Get locale-specific date format and locale
  const getDateFormatAndLocale = () => {
    const currentLang = i18n.language as Language;
    switch (currentLang) {
      case Language.German:
        return { format: "EEEE, d. MMMM", locale: de };
      case Language.French:
        return { format: "EEEE d MMMM", locale: fr };
      case Language.Spanish:
        return { format: "EEEE, d 'de' MMMM", locale: es };
      case Language.Italian:
        return { format: "EEEE d MMMM", locale: it };
      case Language.Portuguese:
        return { format: "EEEE, d 'de' MMMM", locale: pt };
      case Language.Chinese:
        return { format: "M'月'd'日' EEEE", locale: zhCN };
      case Language.English:
      default:
        return { format: "EEEE, MMMM d", locale: enUS };
    }
  };

  const handleReschedule = async () => {
    if (date && timeRange.length === 2) {
      try {
        setIsRescheduling(true);
        // Revalidate available slots through API
        const { data } = await refetch();
        if (data?.data) {
          // Update the time intervals in the store with fresh data
          setTimeIntervals(data.data);
          // Reset the form to first step
          reset?.();
          resetTimeRange();
          resetOtherParticipants();
          setIsConfirmationPage(false);
        }
      }
      catch (e) {
        toast.error({ title: `Failed to refresh time slots, ${e}` });
      }
      finally {
        setIsRescheduling(false);
      }
    }
  };

  return (
    <div className={styles.confirmationWrapper}>
      <div className={styles.confirmationHeader}>
        <img src={bodyHoldingClipboard} alt="boy holding clipboard" />
        <div className={styles.confirmationHeaderText}>
          <Icon icon="CircleCheckmarkFullIcon" size={IconSizes.Size24} color="var(--warning-green-light)" />
          <h2>{t("appointment-confirmation")}</h2>
        </div>
      </div>
      <div className={styles.confirmationBody}>
        <h3>{userAppointmentData.extra?.event_type?.title}</h3>
        <div className={styles.confirmationBodyItemsWrapper}>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="PersonLineIcon" size={IconSizes.Size18} />
            <p>
              {userAppointmentData.extra?.event_type?.user_name}
              ,
              <span>Software Engineer</span>
            </p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="CalendarSimpleLineIcon" size={IconSizes.Size18} />
            <p>
              {!!date && (() => {
                const { format: dateFormat, locale } = getDateFormatAndLocale();
                return format(new Date(date), dateFormat, { locale });
              })()}
            </p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="ClockLineIcon" size={IconSizes.Size18} />
            <p>{!!timeRange && `${timeRange.join(" - ")}`}</p>
          </div>
          <div className={styles.confirmationBodyItem}>
            <Icon icon="GlobeLineIcon" size={IconSizes.Size18} />
            <p>
              {currentTimezone}
              e
            </p>
          </div>
        </div>

        <div className={styles.confirmationSubText}>{t("receive-email")}</div>
        <Button
          icon={PlusIcon}
          width="100%"
          label={t("reschedule")}
          onClick={handleReschedule}
          isLoading={isRescheduling || isApiLoading}
          variant={ButtonVariants.Light}
          color={ButtonColors.Blue}
        />
      </div>
      {/* <MobileFooter
        primaryButton={(
          <Button
            icon={PlusIcon}
            width="100%"
            label={t("reschedule")}
            onClick={handleReschedule}
            isLoading={isRescheduling || isApiLoading}
            variant={ButtonVariants.Light}
            color={ButtonColors.Blue}
          />
        )}
      /> */}
    </div>
  );
}
